import "antd/dist/antd.css";
import "../App.css";

import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import HeroAppPreview from "../components/HeroAppPreview";
import ScreenshotCarousel from "../components/ScreenshotCarousel";
import GuideCard from "../components/GuideCard";
import FaqAccordion from "../components/FaqAccordion";
import usePageMeta from "../usePageMeta";
import { jsonLdString } from "../jsonLd";
import { GUIDES } from "../data/guides";
import { goToApp } from "../siteLinks";

const landingFaqs: { q: string; a: string }[] = [
    {
        q: "Is Planamoto only for motorbike tours?",
        a: "It's built around the way riders plan (daily legs, weather, pre-ride checks), but it works just as well for any group road trip, camper-van tour, or cycling holiday.",
    },
    {
        q: "How do I get my mates on the tour?",
        a: "Each tour has a short join code. Share it and they can hop in once they've signed up. Everyone then sees the same itinerary, expenses, and checklists live.",
    },
    {
        q: "Can we log expenses in different currencies?",
        a: "Yes. Log each cost in the currency you paid. Planamoto converts and settles up at the end, no matter how many borders you crossed.",
    },
    {
        q: "Does it work offline on the road?",
        a: "Not live, but you can export the whole tour as a printable document and keep a paper copy in the tank bag or pannier for when signal drops.",
    },
    {
        q: "How much does it cost?",
        a: "Free while in early release. If we add paid features later, the tours you've already planned stay yours.",
    },
];

const screenshots = [
    { src: "/assets/screenShots/IMG_3091.png", caption: "Overview at a glance" },
    { src: "/assets/screenShots/IMG_3092.png", caption: "Day-by-day itinerary" },
    { src: "/assets/screenShots/IMG_3093.jpg", caption: "See the whole route" },
    { src: "/assets/screenShots/IMG_3095.png", caption: "Shared expenses" },
    { src: "/assets/screenShots/IMG_3098.png", caption: "Group checklists" },
    { src: "/assets/screenShots/IMG_3099.png", caption: "Currency converter" },
];


function Landing() {
    const navigate = useNavigate();
    usePageMeta({
        title: "Planamoto — Plan motorbike tours, cycle tours and group road trips together",
        description:
            "Planamoto is a free tour planner for motorbike riders, cyclists and road-trippers. Plan day-by-day itineraries with weather, split shared expenses across currencies, run group packing and pre-ride checklists, and clone hand-curated routes like the North Coast 500 or the Stelvio Pass loop. Built for groups planning together.",
        url: "https://www.planamoto.com/",
    });

    const orgLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Planamoto",
        url: "https://www.planamoto.com",
        // Google requires a raster logo (PNG/JPG, min 112×112) for logo
        // eligibility — the SVG favicon doesn't qualify.
        logo: "https://www.planamoto.com/logo512.png",
        description:
            "Free tour planner for motorbike riders, cyclists and road-trippers. Group itineraries, shared expenses, currency conversion, and checklists.",
    };
    const siteLd = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Planamoto",
        url: "https://www.planamoto.com",
        publisher: { "@type": "Organization", name: "Planamoto" },
    };
    // SoftwareApplication tells Google (and LLMs building entity graphs)
    // that the primary thing on the page is a tool, not a content site.
    // TravelApplication is the closest applicationCategory to a group
    // trip planner; operatingSystem "Web" signals it runs in the browser
    // rather than as a native install. offers.price "0" reflects the
    // current free-while-in-beta pricing — bump if that ever changes.
    const appLd = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Planamoto",
        url: "https://www.planamoto.com",
        applicationCategory: "TravelApplication",
        operatingSystem: "Web",
        description:
            "Plan group motorbike tours, cycle tours and road trips together — shared itineraries, expenses across currencies, checklists, and printable exports.",
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "GBP",
        },
        publisher: { "@type": "Organization", name: "Planamoto" },
    };
    // No FAQPage JSON-LD here — the canonical FAQPage schema lives on
    // /faq only. Emitting a second, overlapping FAQPage on the homepage
    // reads as duplicate schema across two indexed URLs. The accordion
    // below still renders the Q&As as visible, crawlable text.

    return (
        <div className="gt-landing">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(orgLd) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(siteLd) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(appLd) }}
            />
            {/* HERO — split: copy left, app preview right */}
            <section className="gt-hero-split">
                <div className="gt-hero-split__copy">
                    <div className="gt-eyebrow">
                        Plan on the sofa · Share with the group · Follow on the bike
                    </div>
                    <h1 className="gt-hero-split__title">
                        One tour.<br />
                        Every rider on{" "}
                        <span className="gt-hero-split__accent">
                            the same page.
                        </span>
                    </h1>
                    <p className="gt-hero-split__lede">
                        Planamoto is a free motorbike tour planner for group
                        rides, cycle tours and road trips. Build the itinerary
                        on your laptop, share a join code, and the whole group
                        carries it in their pocket — routes, weather, shared
                        costs and checklists.
                    </p>
                    <div className="gt-hero-split__ctas">
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => goToApp("/signup")}
                        >
                            Get started
                        </Button>
                        <Button size="large" onClick={() => navigate("/explore")}>
                            Explore tours
                        </Button>
                    </div>
                    <p className="gt-hero-split__trust">
                        <span>Free to use</span>
                        <span>No subscription</span>
                        <span>No card required</span>
                    </p>
                </div>
                <div className="gt-hero-split__art">
                    <HeroAppPreview />
                </div>
            </section>

            {/* ROUTE BAND — decorative dashed route */}
            <div className="gt-route-band" aria-hidden="true">
                <svg viewBox="0 0 968 70" width="100%">
                    <path
                        d="M14 50 Q 220 8 440 34 T 860 22 T 954 44"
                        stroke="var(--gt-accent)"
                        strokeWidth="3"
                        strokeDasharray="1 12"
                        strokeLinecap="round"
                        fill="none"
                    />
                    <g fill="var(--gt-primary)">
                        <circle cx="14" cy="50" r="6" />
                        <circle cx="954" cy="44" r="6" />
                    </g>
                    <g stroke="var(--gt-primary)" strokeWidth="2" fill="var(--gt-bg)">
                        <circle cx="440" cy="34" r="5" />
                        <circle cx="860" cy="22" r="5" />
                    </g>
                    <g
                        transform="translate(200,6)"
                        stroke="var(--gt-primary)"
                        strokeWidth="2"
                        fill="none"
                    >
                        <circle cx="10" cy="22" r="6" />
                        <circle cx="34" cy="22" r="6" />
                        <path d="M14 22 L22 12 L30 12 L33 9" strokeLinecap="round" />
                    </g>
                    <text
                        x="14"
                        y="68"
                        fontFamily="ui-monospace, monospace"
                        fontSize="11"
                        fill="var(--gt-text-muted)"
                    >
                        Bilbao
                    </text>
                    <text
                        x="954"
                        y="68"
                        fontFamily="ui-monospace, monospace"
                        fontSize="11"
                        fill="var(--gt-text-muted)"
                        textAnchor="end"
                    >
                        Stelvio Pass
                    </text>
                </svg>
            </div>


            {/* SEE IT IN ACTION */}
            <section className="gt-lp__band gt-band--cream">
                <div className="gt-lp__inner">
                    <div className="gt-band__head">
                        <div className="gt-eyebrow">A look inside</div>
                        <h2 className="gt-band__title">See it in action</h2>
                        <p className="gt-band__intro">
                            A few screens from a real tour. Tap any to zoom in.
                        </p>
                    </div>
                    <ScreenshotCarousel items={screenshots} />
                </div>
            </section>

            {/* GET YOU ROLLING — teal step band */}
            <section className="gt-lp__band gt-band--teal gt-steps-band">
                <div className="gt-lp__inner">
                    <div className="gt-band__head">
                        <div className="gt-eyebrow">Get you rolling</div>
                        <h2 className="gt-band__title">
                            Three steps and you're planning
                        </h2>
                    </div>
                    <ol className="gt-steps">
                        <li className="gt-step">
                            <span className="gt-step__num" aria-hidden="true">
                                1
                            </span>
                            <div>
                                <strong className="gt-step__title">
                                    Create your free account
                                </strong>
                                <p className="gt-step__body">
                                    Takes about a minute. Email and a password,
                                    that's it.
                                </p>
                            </div>
                        </li>
                        <li className="gt-step">
                            <span className="gt-step__num" aria-hidden="true">
                                2
                            </span>
                            <div>
                                <strong className="gt-step__title">
                                    Add your riding partners
                                </strong>
                                <p className="gt-step__body">
                                    Share the join code from the tour and they're
                                    in.
                                </p>
                            </div>
                        </li>
                        <li className="gt-step">
                            <span className="gt-step__num" aria-hidden="true">
                                3
                            </span>
                            <div>
                                <strong className="gt-step__title">
                                    Plan the first day and go
                                </strong>
                                <p className="gt-step__body">
                                    Route, weather, expenses, checklist — all in
                                    one place.
                                </p>
                            </div>
                        </li>
                    </ol>
                    <div className="gt-steps-band__ctas">
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => goToApp("/signup")}
                        >
                            Get started
                        </Button>
                        <Button size="large" onClick={() => navigate("/explore")}>
                            Explore tours
                        </Button>
                    </div>
                </div>
            </section>

            {/* GUIDES */}
            <section className="gt-lp__band gt-band--surface gt-lp-guides">
                <div className="gt-lp__inner">
                    <div className="gt-band__head">
                        <div className="gt-eyebrow">Guides</div>
                        <h2 className="gt-band__title">
                            Practical pieces for the road
                        </h2>
                        <p className="gt-band__intro">
                            What to pack, how the tolls work, what each country
                            expects on the bike.
                        </p>
                    </div>
                    <div className="gt-grid--cards gt-grid--3">
                        {[
                            ...GUIDES.slice(0, 6),
                            // Also feature these three — they live further down
                            // the catalogue, so they wouldn't fall inside the
                            // first-six slice. Nine cards fills the 3-wide grid.
                            ...["europe-trip-cost", "motorbike-tool-kit", "fuel-prices"].map(
                                (slug) => GUIDES.find((g) => g.slug === slug)!,
                            ),
                        ].map((guide) => (
                            <GuideCard
                                key={guide.slug}
                                to={`/guides/${guide.slug}`}
                                eyebrow={`Guide · ${guide.readingTime}`}
                                title={guide.title}
                                body={guide.summary}
                                image={guide.image}
                                icon={guide.icon}
                                accentColor={guide.accentColor}
                            />
                        ))}
                    </div>
                    <div
                        className="gt-body-sm"
                        style={{ textAlign: "center", marginTop: "var(--gt-space-4)" }}
                    >
                        <Link to="/guides">See all guides →</Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="gt-lp__band gt-band--cream gt-lp-faq">
                <div className="gt-lp__inner gt-lp__inner--narrow">
                    <div className="gt-band__head">
                        <div className="gt-eyebrow">Common questions</div>
                        <h2 className="gt-band__title">Quick answers</h2>
                    </div>
                    <FaqAccordion items={landingFaqs} idPrefix="landing-faq" cards />
                    <div
                        className="gt-body-sm"
                        style={{ textAlign: "center", marginTop: "var(--gt-space-4)" }}
                    >
                        <Link to="/faq">See all FAQs →</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Landing;
